import React, { ImgHTMLAttributes, useMemo } from 'react';

import PropTypes from 'prop-types';

import { useTheme } from '@adsk/alloy-react-styles';
import {
  DetailedStylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

import { useDeprecatedIllustration } from './hooks/use-deprecated-illustration';
import { getIllustration } from './illustrations';
import { ILLUSTRATION_TYPES, IllustrationType } from './types';

type IllustrationProps = DetailedStylableComponent<
  ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  type: IllustrationType;
};

/**
 * A component for rendering multi-color images, typically vector illustrations.
 */
const Illustration = React.forwardRef<HTMLImageElement, IllustrationProps>(
  ({ style, className, type, alt = '', ...props }, ref) => {
    const theme = useTheme();
    useDeprecatedIllustration(type);
    const src = useMemo(() => getIllustration(theme, type), [theme, type]);

    return (
      <img
        {...props}
        alt={alt}
        css={[style]}
        className={className}
        ref={ref}
        data-testid={type}
        src={src}
      />
    );
  },
);

Illustration.displayName = 'Illustration';

Illustration.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Illustration type */
  type: PropTypes.oneOf(Object.values(ILLUSTRATION_TYPES)).isRequired,
  /** Illustration height */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Illustration width */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Object.assign(Illustration, { TYPES: ILLUSTRATION_TYPES });
